import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { mediaQueries } from 'shared/styles';
import useMediaQuery from 'shared/hooks/use-media-query';
import { useUI } from 'src/hooks/use-ui';
import RouteLeavingGuard from 'src/routes/route-leaving-guard';
import PublishModal from 'src/dispensary-admin/publish-modal';
import RichTextHtmlWrapper from 'shared/components/rich-text-html-wrapper';
import { ApplyToAll as ApplyToAllIcon } from 'src/svg/apply-to-all';
import { ContentContainer, Detail, Page, Title } from './customize.styles';
import { PublishBanner } from './publish-banner';
import { TextBannerNotificationModal } from './modals/text-banner-notification';
import { PublishToLocationsModal } from './modals/publish-to-locations-modal';

const VIEW_TOGGLE_BREAKPOINT = mediaQueries.custom(1100);

type MenuCustomizationLayoutProps = {
  actionComponent?: JSX.Element;
  headerText: string;
  subHeaderText: string;
  isDirty: boolean;
  handlePublish: () => void;
  handleReset: () => void;
  children: React.ReactNode | React.ReactNode[];
  previewComponent?: JSX.Element;
  /** Form content rendered in a second row below the main form/preview pair */
  secondaryContent?: JSX.Element;
  /** Preview rendered in the right column of the second row, beside secondaryContent */
  secondaryPreviewComponent?: JSX.Element;
  /** Handler for applying settings to selected locations */
  handleApplyToLocations?: (dispensaryIds: string[]) => void;
  /** @deprecated Use handleApplyToLocations instead */
  handleApplyToAllLocations?: () => void;
  hasAccessToAllLocations?: boolean;
  /** Chain ID for the location selector */
  chainId?: string;
  /** Total number of locations in the chain */
  totalLocationCount?: number;
};

export function MenuCustomizationLayout({
  actionComponent,
  headerText,
  subHeaderText,
  isDirty,
  handlePublish,
  handleReset,
  previewComponent,
  secondaryContent,
  secondaryPreviewComponent,
  children,
  handleApplyToLocations,
  handleApplyToAllLocations,
  hasAccessToAllLocations = false,
  chainId: propChainId,
  totalLocationCount: propTotalLocationCount,
}: MenuCustomizationLayoutProps): JSX.Element {
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [activeView, setActiveView] = useState<'form' | 'preview'>('form');
  const isBelowViewToggle = useMediaQuery(VIEW_TOGGLE_BREAKPOINT);
  const UI = useUI();
  const chainId = propChainId ?? UI.dispensary.chain;
  const totalLocationCount = propTotalLocationCount ?? UI.chainDispensaryIds.length;
  const hasApplyHandler = handleApplyToLocations ?? handleApplyToAllLocations;
  const shouldShowApplyToAll = hasApplyHandler && hasAccessToAllLocations && !!chainId;

  const closePublishLocationsModal = (): void => {
    setIsPublishModalOpen(false);
  };

  const openPublishLocationsModal = (): void => {
    setIsPublishModalOpen(true);
  };

  const publishToLocations = (dispensaryIds: string[]): void => {
    setIsPublishing(true);

    const handleResult = (): void => {
      setIsPublishModalOpen(false);
      setIsPublishing(false);
    };

    if (handleApplyToLocations) {
      handleApplyToLocations(dispensaryIds);
      handleResult();
    } else if (handleApplyToAllLocations) {
      // Fallback for backward compatibility - apply to all locations
      handleApplyToAllLocations();
      handleResult();
    } else {
      // No handler provided - close modal for defensive completeness
      handleResult();
    }
  };

  const showViewToggle = isBelowViewToggle && !!previewComponent;
  const showForm = !showViewToggle || activeView === 'form';
  const showPreview = !showViewToggle || activeView === 'preview';

  const viewToggle = (
    <ViewToggleContainer>
      <ViewToggleButton $isActive={activeView === 'form'} onClick={() => setActiveView('form')}>
        Edit
      </ViewToggleButton>
      <ViewToggleButton $isActive={activeView === 'preview'} onClick={() => setActiveView('preview')}>
        Preview
      </ViewToggleButton>
    </ViewToggleContainer>
  );

  return (
    <>
      <Page>
        <TextBannerNotificationModal />

        {showViewToggle && (
          <>
            <HeaderContainer>
              <Title>{headerText}</Title>
              {actionComponent && actionComponent}
            </HeaderContainer>
            <Detail>
              <RichTextHtmlWrapper html={subHeaderText} className='menu-customization-layout' />
            </Detail>
            {viewToggle}
          </>
        )}

        <Container>
          <LeftColumn $hidden={!showForm}>
            {!showViewToggle && (
              <>
                <Container>
                  <Title>{headerText}</Title>
                  {actionComponent && actionComponent}
                </Container>
                <Detail>
                  <RichTextHtmlWrapper html={subHeaderText} className='menu-customization-layout' />
                </Detail>
              </>
            )}

            <ContentContainer>{children}</ContentContainer>
          </LeftColumn>

          {showPreview && (previewComponent || shouldShowApplyToAll) && (
            <RightColumn $fullWidth={showViewToggle}>
              {shouldShowApplyToAll && (
                <ApplyToAllContainer $visible={!isDirty}>
                  <ApplyToAllButton onClick={openPublishLocationsModal}>
                    <ApplyToAllIcon />
                    Apply to other locations
                  </ApplyToAllButton>
                </ApplyToAllContainer>
              )}

              {previewComponent}
            </RightColumn>
          )}
        </Container>

        {(secondaryContent ?? secondaryPreviewComponent) && (
          <SecondaryContainer>
            <LeftColumn $hidden={!showForm}>
              {secondaryContent && <ContentContainer>{secondaryContent}</ContentContainer>}
            </LeftColumn>

            {showPreview && secondaryPreviewComponent && (
              <RightColumn $fullWidth={showViewToggle}>{secondaryPreviewComponent}</RightColumn>
            )}
          </SecondaryContainer>
        )}
      </Page>

      {isDirty && (
        <PublishBanner
          onPublish={handlePublish}
          shouldShowPublishToAll={shouldShowApplyToAll}
          onPublishToAll={openPublishLocationsModal}
          headerText={headerText}
        />
      )}

      <RouteLeavingGuard when={isDirty} modal={PublishModal} handleConfirm={handlePublish} handleDeny={handleReset} />

      {shouldShowApplyToAll && chainId && (
        <PublishToLocationsModal
          isOpen={isPublishModalOpen}
          onClose={closePublishLocationsModal}
          onConfirm={publishToLocations}
          chainId={chainId}
          headerText={headerText}
          totalLocationCount={totalLocationCount}
          loading={isPublishing}
        />
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SecondaryContainer = styled(Container)`
  margin-top: 72px;
`;

const HeaderContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
`;

const LeftColumn = styled.div<{ $hidden?: boolean }>`
  min-width: 0;
  width: 100%;
  ${({ $hidden }) => $hidden && 'display: none;'}
`;

const RightColumn = styled.div<{ $fullWidth?: boolean }>`
  flex-shrink: 0;
  margin-left: 30px;
  width: 370px;

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      align-items: center;
      display: flex;
      flex-direction: column;
      margin-left: 0;
      width: 100%;
    `}
`;

const ViewToggleContainer = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const ViewToggleButton = styled.button<{ $isActive: boolean }>`
  background: #fff;
  border: 2px solid #e0e6ec;
  border-right-width: 0;
  color: #42505d;
  font-size: 12px;
  font-weight: 500;
  min-width: 100px;
  padding: 8px;
  text-transform: uppercase;

  &:first-child {
    border-radius: 50px 0 0 50px;
  }

  &:last-child {
    border-radius: 0 50px 50px 0;
    border-right-width: 2px;
  }

  ${({ $isActive }) =>
    $isActive
      ? css`
          background: #e0e6ec;
          color: #2e3538;
          font-weight: 700;
        `
      : css`
          cursor: pointer;
        `}
`;

const ApplyToAllContainer = styled.div<{ $visible: boolean }>`
  display: flex;
  justify-content: flex-end;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

const ApplyToAllButton = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primaryBlue};
  cursor: pointer;
  display: flex;
  gap: 8px;
  margin-bottom: 35px;
  padding: 0;
`;
